import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumSend } from 'utils/helpers';
import { RequestLicensesModal } from 'new-components/RequestLicenses/RequestLicensesModal';

import { getVisitorPackageId } from '../../../../constants/packageIds';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../store/slices/visitorSearchSlice';
import { fetchVisits, VisitsState } from '../../../../store/slices/visitsSlice';
import { LocationsState } from '../../../../store/slices/locationsSlice';
import STATUS from '../../../../constants/status';
import { useVisitorFeature } from '../../../../hooks/useVisitorFeature';

import VisitorsEditModal from './VisitorsEditModal';

interface VisitorsDrawerHeaderProps {
  onClose: () => void;
  setLoading: (loading: boolean) => void;
  loading: boolean;
}

const VisitorsDrawerHeader = ({
  onClose,
  setLoading,
  loading,
}: VisitorsDrawerHeaderProps) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { isValid: hasAllvisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as number;
  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const locationCount = Array.isArray(locations) ? locations.length : 0;

  const { activeUserCount, totalLicenses } = useVisitorFeature(orgId);

  const licensesLeft = totalLicenses - locationCount;

  const [isVisitorsEditModalOpen, setIsVisitorsEditModalOpen] = useState(false);
  const [isRequestLicensesModalOpen, setIsRequestLicensesModalOpen] =
    useState(false);

  const confirmModal = useConfirmModal();

  const checkLicenseAndProceed = (callback: () => void) => {
    if (locationCount > totalLicenses) {
      confirmModal({
        title: t('License limit reached'),
        content: `You have ${Math.max(0, licensesLeft)} licenses remaining.`,
        okText: t('Request licenses'),
        onOk: () => setIsRequestLicensesModalOpen(true),
      });
      return;
    }

    callback();
  };

  const onDeleteClick = () => {
    confirmModal({
      title: t('visitors.header.confirmDelete.title'),
      content: t('visitors.header.confirmDelete.message'),
      okText: t('visitors.header.confirmDelete.ok'),
      cancelText: t('visitors.header.confirmDelete.cancel'),
      onOk: async () => {
        try {
          await heliumSend(
            'revokeMsiAltaGuestPass',
            [orgId, selectedVisit?.id],
            {},
          );
          await heliumSend('updateVisit', [orgId, selectedVisit?.id], {
            status: 0,
          });

          dispatch(fetchVisits({ orgId }));
          dispatch(searchVisitorsToday({ orgId }));
          if (Object.keys(lastSearchParams).length !== 0) {
            dispatch(
              searchVisitors({
                ...lastSearchParams,
                orgId,
                locationId: lastSearchParams.locationId || globalLocationId!,
              }),
            );
          }
          notificationService.show(
            'success',
            t('visitors.header.notification.deleteSuccess'),
          );
          onClose();
        } catch {
          // Failed to delete visit
        }
      },
    });
  };

  return (
    <>
      <OpSpace>
        {hasAllvisitorsWrite && (
          <>
            {selectedVisit?.visitStatus.id === STATUS.PENDING.id &&
              selectedVisit?.visitors.every(
                (visitor) => visitor.status === 1,
              ) && (
                <OpButton
                  type="primary"
                  danger
                  ghost
                  onClick={() => checkLicenseAndProceed(onDeleteClick)}
                  disabled={loading}
                >
                  {t('visitors.header.delete')}
                </OpButton>
              )}
            <OpButton
              type="primary"
              ghost
              onClick={() =>
                checkLicenseAndProceed(() => setIsVisitorsEditModalOpen(true))
              }
              disabled={loading}
            >
              {t('visitors.header.edit')}
            </OpButton>
          </>
        )}
      </OpSpace>

      {isVisitorsEditModalOpen && (
        <VisitorsEditModal
          open={isVisitorsEditModalOpen}
          onClose={() => setIsVisitorsEditModalOpen(false)}
          setLoading={setLoading}
        />
      )}
      <RequestLicensesModal
        orgId={orgId}
        featureName={t('Visitor')}
        modalOpen={isRequestLicensesModalOpen}
        onClose={() => setIsRequestLicensesModalOpen(false)}
        packageId={getVisitorPackageId(activeUserCount)}
      />
    </>
  );
};

export default VisitorsDrawerHeader;
